/**
 * Group LMS chapters under subjects (matches Flutter / builder data model).
 */

export const normalizeSubjectKey = (value) => {
  if (value == null || value === "") return null;
  return String(value).trim();
};

export const chapterBelongsToSubject = (chapter, subject) => {
  const chapterSubjectId = normalizeSubjectKey(
    chapter?.subjectId ?? chapter?.subject_id,
  );
  const subjectDbId = normalizeSubjectKey(subject?.dbId);
  const subjectId = normalizeSubjectKey(subject?.id);

  if (chapterSubjectId) {
    if (subjectDbId && chapterSubjectId === subjectDbId) return true;
    if (subjectId && chapterSubjectId === subjectId) return true;
  }

  const chapterName = (
    chapter?.subjectName ||
    chapter?.subject_name ||
    "General"
  ).trim();
  const subjectName = (subject?.name || "General").trim();
  return chapterName.toLowerCase() === subjectName.toLowerCase();
};

const sortSubjects = (subjects) =>
  [...subjects].sort((a, b) => {
    const orderA = Number(a.subject_order ?? a.subjectOrder ?? 0);
    const orderB = Number(b.subject_order ?? b.subjectOrder ?? 0);
    if (orderA !== orderB) return orderA - orderB;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });

/**
 * @param {Array} chapters
 * @param {Array} subjects - from API or derived
 * @param {{ includeEmptySubjects?: boolean }} options
 */
export const groupChaptersBySubject = (
  chapters = [],
  subjects = [],
  { includeEmptySubjects = false } = {},
) => {
  const safeChapters = Array.isArray(chapters) ? chapters : [];
  const safeSubjects = Array.isArray(subjects) ? subjects : [];

  const buildFromChaptersOnly = () => {
    const groups = new Map();
    safeChapters.forEach((chapter) => {
      const name = (
        chapter.subjectName ||
        chapter.subject_name ||
        "General"
      ).trim();
      const key =
        normalizeSubjectKey(chapter.subjectId ?? chapter.subject_id) ||
        name.toLowerCase();
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          dbId: chapter.subjectId ?? chapter.subject_id ?? null,
          name,
          chapters: [],
        });
      }
      groups.get(key).chapters.push(chapter);
    });
    return sortSubjects(Array.from(groups.values()));
  };

  if (safeSubjects.length === 0) {
    return buildFromChaptersOnly();
  }

  const grouped = sortSubjects(
    safeSubjects.map((subject) => ({
      ...subject,
      id: subject.id ?? subject.dbId ?? subject.name,
      dbId: subject.dbId ?? subject.id ?? null,
      name: subject.name || "General",
      chapters: safeChapters.filter((chapter) =>
        chapterBelongsToSubject(chapter, subject),
      ),
    })),
  );

  const assignedChapterIds = new Set();
  grouped.forEach((subject) => {
    subject.chapters.forEach((chapter) => {
      assignedChapterIds.add(chapter.id);
    });
  });

  const orphanChapters = safeChapters.filter(
    (chapter) => !assignedChapterIds.has(chapter.id),
  );

  if (orphanChapters.length > 0) {
    const orphanGroups = buildFromChaptersOnly().filter((subject) =>
      subject.chapters.some((chapter) => orphanChapters.includes(chapter)),
    );

    orphanGroups.forEach((orphanSubject) => {
      const existing = grouped.find(
        (subject) =>
          String(subject.name || "").toLowerCase() ===
          String(orphanSubject.name || "").toLowerCase(),
      );
      if (existing) {
        orphanSubject.chapters.forEach((chapter) => {
          if (!existing.chapters.some((ch) => ch.id === chapter.id)) {
            existing.chapters.push(chapter);
          }
        });
      } else {
        grouped.push(orphanSubject);
      }
    });
  }

  if (includeEmptySubjects) {
    return grouped;
  }

  return grouped.filter((subject) => subject.chapters.length > 0);
};
